import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Slider,
  TextField,
  MenuItem,
  Divider,
  Paper
} from '@mui/material';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import editFill from '@iconify/icons-eva/edit-fill';
// components
import { TextBase } from 'components';
import ColorPickerBase from 'components/controls/ColorPickerBase';
// hooks
import useTheme from '../../hooks/useTheme';
import { withNotification } from 'wrappers';

// ----------------------------------------------------------------------

const validationSchema = Yup.object({
  name: Yup.string().required('El nombre del tema es requerido'),
});

const fontFamilies = [
  'Roboto',
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Verdana',
];

function ThemeEditorComponent({ open, onClose, theme, appSuccess, appError }) {
  const { createTheme, updateTheme, loading } = useTheme();
  const [previewConfig, setPreviewConfig] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize preview config when dialog opens
  // Preserve all original config values to avoid losing data
  const initialValues = theme
    ? {
      name: theme.name || '',
      config: {
        // Preserve the entire config structure, only use defaults if missing
        ...theme.config,
        colors: {
          ...theme.config?.colors,
          primary: {
            ...theme.config?.colors?.primary,
            lighter: theme.config?.colors?.primary?.lighter ?? '#E3F2FD',
            light: theme.config?.colors?.primary?.light ?? '#90CAF9',
            main: theme.config?.colors?.primary?.main ?? '#2196F3',
            dark: theme.config?.colors?.primary?.dark ?? '#1976D2',
            darker: theme.config?.colors?.primary?.darker ?? '#0D47A1',
            contrastText: theme.config?.colors?.primary?.contrastText ?? '#FFFFFF',
          },
          background: {
            ...theme.config?.colors?.background,
            default: theme.config?.colors?.background?.default ?? '#FFFFFF',
            paper: theme.config?.colors?.background?.paper ?? '#FFFFFF',
            neutral: theme.config?.colors?.background?.neutral ?? '#F5F5F5',
          },
          text: {
            ...theme.config?.colors?.text,
            primary: theme.config?.colors?.text?.primary ?? '#212B36',
            secondary: theme.config?.colors?.text?.secondary ?? '#637381',
            disabled: theme.config?.colors?.text?.disabled ?? '#919EAB',
          },
        },
        typography: {
          ...theme.config?.typography,
          fontFamily: Array.isArray(theme.config?.typography?.fontFamily)
            ? theme.config.typography.fontFamily[0]
            : (theme.config?.typography?.fontFamily ?? 'Roboto'),
          fontSize: Array.isArray(theme.config?.typography?.fontSize)
            ? theme.config.typography.fontSize[0]
            : (typeof theme.config?.typography?.fontSize === 'number' ? theme.config.typography.fontSize : 14),
        },
        spacing: {
          ...theme.config?.spacing,
          base: Array.isArray(theme.config?.spacing?.base)
            ? theme.config.spacing.base[0]
            : (typeof theme.config?.spacing?.base === 'number' ? theme.config.spacing.base : 4),
        },
        borderRadius: Array.isArray(theme.config?.borderRadius)
          ? theme.config.borderRadius[0]
          : (typeof theme.config?.borderRadius === 'number' ? theme.config.borderRadius : 6),
        iconSize: Array.isArray(theme.config?.iconSize)
          ? theme.config.iconSize[0]
          : (typeof theme.config?.iconSize === 'number' ? theme.config.iconSize : 20),
        mode: theme.config?.mode ?? 'light',
      },
    }
    : {
      name: '',
      config: {
        colors: {
          primary: {
            lighter: '#E3F2FD',
            light: '#90CAF9',
            main: '#2196F3',
            dark: '#1976D2',
            darker: '#0D47A1',
            contrastText: '#FFFFFF',
          },
          background: {
            default: '#FFFFFF',
            paper: '#FFFFFF',
            neutral: '#F5F5F5',
          },
          text: {
            primary: '#212B36',
            secondary: '#637381',
            disabled: '#919EAB',
          },
        },
        typography: {
          fontFamily: 'Roboto',
          fontSize: 14,
        },
        spacing: {
          base: 4,
        },
        borderRadius: 6,
        iconSize: 20,
        mode: 'light',
      },
    };

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      if (theme) {
        await updateTheme(theme.id, values);
        appSuccess('Tema actualizado exitosamente');
      } else {
        await createTheme(values);
        appSuccess('Tema creado exitosamente');
      }
      onClose();
    } catch (error) {
      appError(error.message || 'Error al guardar el tema');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Initialize preview config when dialog opens
  useEffect(() => {
    if (open) {
      setPreviewConfig(initialValues.config);
    } else {
      setPreviewConfig(null);
    }
  }, [open, theme]);


  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      aria-labelledby="theme-editor-dialog-title"
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
          // Normalize config values to ensure they're not arrays
          const normalizeValue = (value, defaultValue) => {
            if (Array.isArray(value)) return value[0];
            if (typeof value === 'number') return value;
            return defaultValue;
          };

          // Use values.config directly for preview but normalize array values
          const displayConfig = {
            ...values.config,
            typography: {
              ...values.config.typography,
              fontSize: normalizeValue(values.config.typography?.fontSize, 14),
            },
            spacing: {
              ...values.config.spacing,
              base: normalizeValue(values.config.spacing?.base, 4),
            },
            borderRadius: normalizeValue(values.config.borderRadius, 6),
            iconSize: normalizeValue(values.config.iconSize, 20),
          };

          return (
            <Form>
              <DialogTitle id="theme-editor-dialog-title">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">
                    {theme ? 'Editar Tema' : 'Crear Nuevo Tema'}
                  </Typography>
                  <Button
                    onClick={onClose}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    <Icon icon={closeFill} width={20} height={20} />
                  </Button>
                </Box>
              </DialogTitle>

              <DialogContent dividers>
                <Grid container spacing={3}>
                  {/* Basic Info */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Información Básica
                    </Typography>
                    <Field
                      label="Nombre del Tema"
                      name="name"
                      component={TextBase}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Colors Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Colores Principales
                    </Typography>
                  </Grid>

                  {/* Primary Colors */}
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                      Color Primario
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Field
                          label="Principal"
                          name="config.colors.primary.main"
                          component={ColorPickerBase}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label="Oscuro"
                          name="config.colors.primary.dark"
                          component={ColorPickerBase}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label="Claro"
                          name="config.colors.primary.light"
                          component={ColorPickerBase}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Field
                          label="Muy Claro"
                          name="config.colors.primary.lighter"
                          component={ColorPickerBase}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Background Colors */}
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ mb: 1, display: 'block', mt: 2 }}>
                      Colores de Fondo
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Field
                          label="Fondo Principal"
                          name="config.colors.background.default"
                          component={ColorPickerBase}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Field
                          label="Fondo de Cards"
                          name="config.colors.background.paper"
                          component={ColorPickerBase}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Field
                          label="Fondo Neutral"
                          name="config.colors.background.neutral"
                          component={ColorPickerBase}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Text Colors */}
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ mb: 1, display: 'block', mt: 2 }}>
                      Colores de Texto
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Field
                          label="Texto Principal"
                          name="config.colors.text.primary"
                          component={ColorPickerBase}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Field
                          label="Texto Secundario"
                          name="config.colors.text.secondary"
                          component={ColorPickerBase}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Field
                          label="Texto Deshabilitado"
                          name="config.colors.text.disabled"
                          component={ColorPickerBase}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Typography Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Tipografía
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          select
                          fullWidth
                          label="Familia de Fuente"
                          value={values.config.typography.fontFamily}
                          onChange={(e) =>
                            setFieldValue('config.typography.fontFamily', e.target.value)
                          }
                          size="small"
                        >
                          {fontFamilies.map((font) => (
                            <MenuItem key={font} value={font}>
                              <Box component="span" sx={{ fontFamily: font }}>
                                {font}
                              </Box>
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                          Tamaño de Fuente: {Array.isArray(values.config.typography.fontSize) ? values.config.typography.fontSize[0] : values.config.typography.fontSize}px
                        </Typography>
                        <Slider
                          value={Array.isArray(values.config.typography.fontSize) ? values.config.typography.fontSize[0] : (typeof values.config.typography.fontSize === 'number' ? values.config.typography.fontSize : 14)}
                          onChange={(e, newValue) =>
                            setFieldValue('config.typography.fontSize', newValue)
                          }
                          min={10}
                          max={20}
                          step={1}
                          marks
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Spacing & Border Radius */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Espaciado y Bordes
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                          Espaciado Base: {Array.isArray(values.config.spacing.base) ? values.config.spacing.base[0] : values.config.spacing.base}px
                        </Typography>
                        <Slider
                          value={Array.isArray(values.config.spacing.base) ? values.config.spacing.base[0] : (typeof values.config.spacing.base === 'number' ? values.config.spacing.base : 4)}
                          onChange={(e, newValue) =>
                            setFieldValue('config.spacing.base', newValue)
                          }
                          min={2}
                          max={8}
                          step={1}
                          marks
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                          Radio de Borde: {Array.isArray(values.config.borderRadius) ? values.config.borderRadius[0] : values.config.borderRadius}px
                        </Typography>
                        <Slider
                          value={Array.isArray(values.config.borderRadius) ? values.config.borderRadius[0] : (typeof values.config.borderRadius === 'number' ? values.config.borderRadius : 6)}
                          onChange={(e, newValue) =>
                            setFieldValue('config.borderRadius', newValue)
                          }
                          min={0}
                          max={16}
                          step={1}
                          marks
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Icon Size */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Tamaño de Iconos
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                          Tamaño de Iconos: {Array.isArray(values.config.iconSize) ? values.config.iconSize[0] : values.config.iconSize}px
                        </Typography>
                        <Slider
                          value={Array.isArray(values.config.iconSize) ? values.config.iconSize[0] : (typeof values.config.iconSize === 'number' ? values.config.iconSize : 20)}
                          onChange={(e, newValue) =>
                            setFieldValue('config.iconSize', newValue)
                          }
                          min={16}
                          max={32}
                          step={2}
                          marks
                        />
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                          Afecta el tamaño de los iconos de acción (editar, eliminar, etc.)
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  {/* Preview Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Vista Previa
                    </Typography>
                    <Paper
                      sx={{
                        p: 3,
                        bgcolor: displayConfig?.colors?.background?.default || '#FFFFFF',
                        minHeight: 150,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            p: displayConfig?.spacing?.base ? `${displayConfig.spacing.base * 2}px` : '16px',
                            borderRadius: `${displayConfig?.borderRadius || 6}px`,
                            bgcolor: displayConfig?.colors?.background?.paper || '#FFFFFF',
                            border: `1px solid ${displayConfig?.colors?.text?.secondary || '#637381'}`,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: displayConfig?.colors?.text?.primary || '#212B36',
                              fontFamily: displayConfig?.typography?.fontFamily || 'Roboto',
                              fontSize: `${displayConfig?.typography?.fontSize || 14}px`,
                              mb: displayConfig?.spacing?.base ? `${displayConfig.spacing.base}px` : '8px',
                            }}
                          >
                            Título de Ejemplo
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: displayConfig?.colors?.text?.secondary || '#637381',
                              fontFamily: displayConfig?.typography?.fontFamily || 'Roboto',
                              fontSize: `${displayConfig?.typography?.fontSize || 14}px`,
                              mb: displayConfig?.spacing?.base ? `${displayConfig.spacing.base * 2}px` : '16px',
                            }}
                          >
                            Este es un texto de ejemplo para mostrar cómo se verá el tema.
                          </Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: displayConfig?.spacing?.base ? `${displayConfig.spacing.base}px` : '8px',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <Button
                              variant="contained"
                              sx={{
                                bgcolor: displayConfig?.colors?.primary?.main || '#2196F3',
                                color: displayConfig?.colors?.primary?.contrastText || '#FFFFFF',
                                fontFamily: displayConfig?.typography?.fontFamily || 'Roboto',
                                fontSize: `${displayConfig?.typography?.fontSize || 14}px`,
                                borderRadius: `${displayConfig?.borderRadius || 6}px`,
                                '&:hover': {
                                  bgcolor: displayConfig?.colors?.primary?.dark || '#1976D2',
                                },
                              }}
                            >
                              Botón de Ejemplo
                            </Button>

                            {/* Icon Preview */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1,
                                borderRadius: `${displayConfig?.borderRadius || 6}px`,
                                bgcolor: displayConfig?.colors?.background?.neutral || '#F5F5F5',
                                border: `1px solid ${displayConfig?.colors?.text?.secondary || '#637381'}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: displayConfig?.colors?.text?.secondary || '#637381',
                                  fontFamily: displayConfig?.typography?.fontFamily || 'Roboto',
                                  fontSize: `${(displayConfig?.typography?.fontSize || 14) * 0.85}px`,
                                }}
                              >
                                Icono:
                              </Typography>
                              <Icon
                                icon={editFill}
                                width={displayConfig?.iconSize || 20}
                                height={displayConfig?.iconSize || 20}
                                style={{
                                  color: displayConfig?.colors?.primary?.main || '#2196F3',
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: displayConfig?.colors?.text?.secondary || '#637381',
                                  fontFamily: displayConfig?.typography?.fontFamily || 'Roboto',
                                  fontSize: `${(displayConfig?.typography?.fontSize || 14) * 0.85}px`,
                                }}
                              >
                                {displayConfig?.iconSize || 20}px
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting || loading}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting || loading ? 'Guardando...' : (theme ? 'Actualizar' : 'Crear')}
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}

ThemeEditorComponent.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  theme: PropTypes.object,
  appSuccess: PropTypes.func,
  appError: PropTypes.func,
};

const ThemeEditor = withNotification(ThemeEditorComponent);

export default ThemeEditor;

